import { useState, type ReactNode } from 'react';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  INVOICE_CREDIT_TARGETS,
  INVOICE_CREDIT_TARGET_HINTS,
  INVOICE_CREDIT_TARGET_LABELS,
  TENANCY_INVOICE_STATE_LABELS,
  formatNzDate,
  formatRef,
  nzDate,
  parseRef,
  tenancyInvoiceDaysOverdue,
  tenancyInvoiceDaysUntilDue,
  tenancyInvoiceRedirectTotals,
  type InvoiceCreditTarget,
} from '@promanage/core';
import {
  Amount,
  Button,
  Chip,
  Field,
  Panel,
  PanelHeader,
  Ref,
  Select,
  Textarea,
} from '@promanage/ui';
import { RecordNotFound } from '../components/RecordNotFound';
import { RecordIdentity } from '../components/RecordIdentity';
import { RecordSkeleton } from '../components/RecordSkeleton';
import { TopBar } from '../components/TopBar';
import { Icon } from '../components/Icon';
import type { TenancyInvoiceDetail } from '../features/tenancyInvoices/types';
import { InvoicePdfButton } from '../features/tenancyInvoices/InvoicePdfButton';
import { InvoiceHistory } from '../features/tenancyInvoices/InvoiceHistory';
import { InvoiceSends } from '../features/tenancyInvoices/InvoiceSends';
import { trpc } from '../lib/trpc';
import { SaveButton, useUnsavedChanges } from '../components/UnsavedChanges';

/**
 * One tenancy invoice, addressed by its display ref: /bills/invoices/IV000004.
 *
 * Same ref contract as /bills/$ref — `beforeLoad` normalises anything ref-shaped to the canonical
 * form, and the component answers each remaining wrong shape distinctly.
 *
 * SENDING IT TO THE TENANTS is offered until it is withdrawn — see `tenancyInvoices.send`.
 *
 * TWO CORRECTIONS ARE OFFERED, and both only while nothing has been paid: where the money goes,
 * and withdrawing the charge altogether. They are the two things an agency finds out
 * late about an invoice, and the first is much the commoner — a recovery keyed to the agency
 * produces exactly the document the tenant expects, so nothing surfaces the mistake until an owner
 * asks where their reimbursement went.
 *
 * IT CANNOT OFFER "TAKE THE PAYMENT", because a payment is a bank deposit that may cover rent and
 * two invoices at once — that belongs on the generator, where a deposit is entered as the one
 * event it was. Putting a "mark paid" button here would invite an operator to record money
 * arriving on a screen that has no idea what else arrived with it.
 */
export const Route = createFileRoute('/bills/invoices/$ref')({
  beforeLoad: ({ params }) => {
    const parsed = parseRef(params.ref);
    if (parsed?.entity === 'invoice') {
      const canonical = formatRef('invoice', parsed.sequence);
      // Loop-safe: formatRef of a parsed canonical ref is that same string.
      if (canonical !== params.ref) {
        throw redirect({
          to: '/bills/invoices/$ref',
          params: { ref: canonical },
          replace: true,
        });
      }
    }
  },
  component: TenancyInvoicePage,
});

function TenancyInvoicePage() {
  const { ref } = Route.useParams();
  const parsed = parseRef(ref);

  const record = useQuery(
    trpc.tenancyInvoices.get.queryOptions(
      { refSeq: parsed?.sequence ?? 0 },
      { enabled: parsed?.entity === 'invoice', staleTime: 10_000 },
    ),
  );

  if (parsed === null || parsed.entity !== 'invoice') {
    return (
      <NotFound
        title={parsed === null ? `“${ref}” is not a record reference` : 'That is not an invoice'}
      >
        {parsed === null
          ? 'A tenancy invoice reference looks like IV000004.'
          : `${ref} belongs to another kind of record. Tenancy invoice references start with IV.`}
      </NotFound>
    );
  }

  if (record.isPending) {
    return (
      <div className="mx-auto max-w-[var(--content-max)] pb-4">
        <TopBar title="Tenancy invoice" subtitle="Loading…" />
        <RecordSkeleton />
      </div>
    );
  }

  if (record.error || !record.data) {
    return (
      <NotFound title="No such invoice">
        {`${ref} is not a tenancy invoice in this agency.`}
      </NotFound>
    );
  }

  return <Loaded invoice={record.data} />;
}

const STATE_TONE: Record<
  TenancyInvoiceDetail['state'],
  'neutral' | 'accent' | 'positive' | 'warn'
> = {
  outstanding: 'warn',
  part_paid: 'accent',
  paid: 'positive',
  cancelled: 'neutral',
};

function Loaded({ invoice }: { invoice: TenancyInvoiceDetail }) {
  const queryClient = useQueryClient();
  const today = nzDate(new Date());
  const [creditTo, setCreditTo] = useState<InvoiceCreditTarget>(invoice.creditTo);
  // A destination picked and not saved is the one edit on this page that can be lost.
  const destination = useUnsavedChanges(creditTo !== invoice.creditTo);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: trpc.tenancyInvoices.get.queryKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.tenancyInvoices.list.queryKey() });
  };

  const redirect = useMutation(
    trpc.tenancyInvoices.redirect.mutationOptions({
      meta: { errorTitle: 'Could not change where that invoice goes' },
      onSuccess: () => {
        destination.saved();
        refresh();
      },
    }),
  );

  /*
   * WHETHER GST GOES ON TOP IS THE SERVER'S TO DECIDE, and this reads the setting only so the
   * panel can show what the tenant will owe before somebody commits to it — the same restraint
   * `IssueInvoiceForm` keeps. `redirectTenancyInvoice` recomputes all three figures from
   * `organisations.gst_registered` inside the write.
   */
  const settings = useQuery(trpc.org.settings.queryOptions(undefined, { staleTime: 60_000 }));
  const gstRegistered = settings.data?.gstRegistered ?? false;

  const total = BigInt(invoice.totalCents);
  const outstanding = BigInt(invoice.outstandingCents);
  const open = invoice.state === 'outstanding' || invoice.state === 'part_paid';
  const daysOverdue = open ? tenancyInvoiceDaysOverdue(invoice.dueOn, today) : 0;

  // Nothing paid, and not withdrawn. Both actions on this page need exactly this - see
  // `tenancyInvoiceRedirectIssues` and `tenancyInvoiceCancelIssues`, which refuse a part payment
  // as firmly as a settled invoice.
  const untouched = invoice.state === 'outstanding';

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      {/*
       * THE HOUSE IS THE HEADING, as it is on every tenancy-side record; what the charge is for
       * sits under it. A free-text description is whatever the operator typed — "testing", or a
       * sentence — and reads badly as a page title.
       */}
      <TopBar
        title={invoice.propertyAddress}
        subtitle={invoice.description}
        actions={
          <>
            <InvoicePdfButton refSeq={invoice.refSeq} />
            <Link to="/bills" search={{ tab: 'tenancy' }}>
              <Button size="sm" variant="secondary">
                All tenancy invoices
              </Button>
            </Link>
          </>
        }
      />

      <div className="flex flex-col gap-4">
        {/*
         * THE SHARED IDENTITY CARD, as on the bill a charge like this often recovers. The figure is
         * the title because it is what the invoice IS. Six facts, two even rows — a seventh sat
         * alone on a third row and stretched the card.
         */}
        <RecordIdentity
          title={<Amount value={total} currency tone="neutral" />}
          symbol={<Icon name="invoice" className="size-6" />}
          status={
            <>
              <Ref entity="invoice" sequence={invoice.refSeq} />
              <Chip tone={STATE_TONE[invoice.state]}>
                {TENANCY_INVOICE_STATE_LABELS[invoice.state]}
              </Chip>
              {daysOverdue > 0 ? (
                <Chip tone="warn">
                  Overdue {daysOverdue} {daysOverdue === 1 ? 'day' : 'days'}
                </Chip>
              ) : null}
            </>
          }
          subtitle={
            <>
              {BigInt(invoice.gstCents) > 0n ? (
                <>
                  Including <Amount value={BigInt(invoice.gstCents)} currency tone="neutral" /> GST
                </>
              ) : (
                'No GST — a recovery is not a supply'
              )}
              {' · raised by '}
              {invoice.issuedByName ?? <span className="text-ink-subtle">somebody unknown</span>}
            </>
          }
          facts={[
            {
              label: 'Tenancy',
              value: (
                <Link
                  to="/tenancies/$ref"
                  params={{ ref: invoice.tenancyRef }}
                  className="hover:text-accent hover:underline"
                >
                  {invoice.tenancyRef}
                </Link>
              ),
            },
            {
              label: 'Property',
              value: (
                <Link
                  to="/properties/$ref"
                  params={{ ref: invoice.propertyRef }}
                  className="hover:text-accent hover:underline"
                >
                  {invoice.propertyAddress}
                </Link>
              ),
            },
            { label: 'Money goes to', value: <Destination invoice={invoice} /> },
            { label: 'Issued', value: formatNzDate(invoice.issuedOn) },
            {
              label: 'Due',
              value: (
                <>
                  <span className={daysOverdue > 0 ? 'text-warn' : undefined}>
                    {formatNzDate(invoice.dueOn)}
                  </span>
                  {open ? (
                    <span className="block text-[0.75rem] font-normal text-ink-muted">
                      {dueWhen(invoice.dueOn, today)}
                    </span>
                  ) : null}
                </>
              ),
            },
            {
              label: 'Recovers',
              value:
                invoice.billRef === null ? (
                  // Not missing data. Most charges recover nothing — a lease break fee has no
                  // invoice behind it.
                  <span className="font-normal text-ink-subtle">Not a bill recovery</span>
                ) : (
                  <Link
                    to="/bills/$ref"
                    params={{ ref: invoice.billRef }}
                    className="hover:text-accent hover:underline"
                  >
                    {invoice.billRef}
                  </Link>
                ),
            },
          ]}
        />

        <div className="grid gap-4 lg:grid-cols-3">
          <div className="flex min-w-0 flex-col gap-4 lg:col-span-2">
            <Standing invoice={invoice} outstanding={outstanding} daysOverdue={daysOverdue} />
            <InvoiceSends invoice={invoice} />
          </div>

          <div className="flex min-w-0 flex-col gap-4">
            {invoice.state === 'cancelled' ? (
              <Panel className="p-5">
                <PanelHeader title="Withdrawn" className="p-0 pb-3" />
                <p className="text-[0.8125rem] text-ink">{invoice.cancelledReason}</p>
                <p className="mt-1 text-[0.75rem] text-ink-muted">
                  {invoice.cancelledByName ? `By ${invoice.cancelledByName}` : 'By somebody'}
                  {invoice.cancelledAt === null
                    ? null
                    : ` · ${formatNzDate(nzDate(new Date(invoice.cancelledAt)))}`}
                </p>
                <p className="mt-3 text-[0.8125rem] text-ink-muted">
                  The number stays spent. If the charge is payable after all, raise a new invoice.
                </p>
              </Panel>
            ) : untouched ? (
              <>
                <RedirectPanel
                  justSaved={destination.justSaved}
                  invoice={invoice}
                  creditTo={creditTo}
                  setCreditTo={setCreditTo}
                  gstRegistered={gstRegistered}
                  pending={redirect.isPending}
                  onConfirm={() => {
                    redirect.mutate({ tenancyInvoiceId: invoice.id, creditTo });
                  }}
                />
                <WithdrawPanel invoice={invoice} onWithdrawn={refresh} />
              </>
            ) : (
              <Panel className="p-5">
                <PanelHeader title="Money has arrived" className="p-0 pb-3" />
                <p className="text-[0.8125rem] text-ink-muted">
                  Its destination is fixed and it can no longer be withdrawn — the journals exist.
                  If the charge was wrong, reverse the receipt or refund the tenant.
                </p>
              </Panel>
            )}

            <InvoiceHistory invoice={invoice} />
          </div>
        </div>
      </div>
    </div>
  );
}

/** "in 13 days", "today", "12 days overdue" — the due date as somebody chasing it reads it. */
function dueWhen(dueOn: string, today: string): string {
  const days = tenancyInvoiceDaysUntilDue(dueOn, today);
  if (days === 0) return 'Due today';
  if (days > 0) return days === 1 ? 'Tomorrow' : `In ${days} days`;
  const late = tenancyInvoiceDaysOverdue(dueOn, today);
  return late === 1 ? '1 day overdue' : `${late} days overdue`;
}

/**
 * Who is credited when it is paid — by NAME and share, on this page.
 *
 * The tenant's copy says "the owner" on purpose (`INVOICE_CREDIT_TARGET_LABELS`), but the operator
 * checking a recovery on a jointly owned house needs to see it will be split, and between whom,
 * before the money lands. These are the owners on the ISSUE date — the ones the payment path
 * credits — so a house sold since still shows the seller.
 */
function Destination({ invoice }: { invoice: TenancyInvoiceDetail }) {
  const changed =
    invoice.redirectedAt === null ? null : (
      // On the record, not only in the history. An invoice whose destination is not the one it
      // was issued with is something the next person to open this page has to see.
      <span className="block text-[0.75rem] font-normal text-ink-muted">
        changed by {invoice.redirectedByName ?? 'somebody'}{' '}
        {formatNzDate(invoice.redirectedAt.slice(0, 10))}
      </span>
    );

  if (invoice.creditTo !== 'owner' || invoice.owners.length === 0) {
    return (
      <>
        {INVOICE_CREDIT_TARGET_LABELS[invoice.creditTo]}
        {changed}
      </>
    );
  }

  const shared = invoice.owners.length > 1;
  return (
    <>
      {invoice.owners.map((owner) => (
        <span key={owner.ownerId} className="block">
          <Link
            to="/owners/$ref"
            params={{ ref: owner.ownerRef }}
            className="hover:text-accent hover:underline"
          >
            {owner.ownerName}
          </Link>
          {shared ? (
            <span className="font-normal text-ink-muted"> · {formatShare(owner.shareBp)}</span>
          ) : null}
        </span>
      ))}
      {changed}
    </>
  );
}

/** 5000 → "50%", 3333 → "33.33%". Basis points, so two decimals is the most there can be. */
function formatShare(shareBp: number): string {
  return `${String(Number((shareBp / 100).toFixed(2)))}%`;
}

/**
 * Where the invoice stands — owing, due, paid — and the payments against it.
 *
 * THE WIDE COLUMN'S FIRST PANEL, so it has to say something before any money arrives: what is
 * owing and by when is the question every visit to an unpaid invoice is asking.
 *
 * REVERSED ROWS STAY, struck through and labelled — the same rule a bill's postings follow.
 * Reversing the receipt genuinely puts the invoice back to outstanding, and the row that says
 * somebody paid it once is exactly what an operator chasing it needs to see.
 */
function Standing({
  invoice,
  outstanding,
  daysOverdue,
}: {
  invoice: TenancyInvoiceDetail;
  outstanding: bigint;
  daysOverdue: number;
}) {
  const paid = BigInt(invoice.settledCents);

  return (
    <Panel className="p-5">
      <PanelHeader title="Where it stands" className="p-0 pb-3" />

      <dl className="grid gap-3 sm:grid-cols-3">
        <Tile label="Owing">
          <Amount
            value={outstanding}
            currency
            tone={outstanding > 0n ? 'neutral' : 'positive'}
            className="text-[1.125rem] font-bold"
          />
        </Tile>
        <Tile label="Paid">
          <Amount value={paid} currency tone="neutral" className="text-[1.125rem] font-bold" />
        </Tile>
        <Tile label={daysOverdue > 0 ? 'Overdue' : 'Due'}>
          <span
            className={
              daysOverdue > 0 ? 'text-[1.125rem] font-bold text-warn' : 'text-[1.125rem] font-bold'
            }
          >
            {invoice.state === 'paid'
              ? 'Settled'
              : invoice.state === 'cancelled'
                ? 'Withdrawn'
                : daysOverdue > 0
                  ? `${daysOverdue} ${daysOverdue === 1 ? 'day' : 'days'}`
                  : formatNzDate(invoice.dueOn)}
          </span>
        </Tile>
      </dl>

      <h3 className="mt-5 mb-2 text-[0.8125rem] font-semibold">Payments</h3>
      {invoice.payments.length === 0 ? (
        <p className="text-[0.8125rem] text-ink-muted">
          None yet. A payment is receipted on the Financial page, with the deposit it arrives in.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {invoice.payments.map((payment) => (
            <li
              key={payment.id}
              className="flex flex-wrap items-baseline justify-between gap-2 border-b border-rule pb-2 last:border-0 last:pb-0"
            >
              <div className="min-w-0">
                <span
                  className={
                    payment.reversed ? 'font-medium text-ink-subtle line-through' : 'font-medium'
                  }
                >
                  Paid
                </span>
                <span className="text-[0.8125rem] text-ink-muted">
                  {' '}
                  {formatNzDate(payment.occurredOn)}
                </span>
                {payment.reversed ? (
                  <Chip tone="neutral" className="ml-2">
                    Reversed
                    {payment.reversedOn === null ? '' : ` ${formatNzDate(payment.reversedOn)}`}
                  </Chip>
                ) : null}
              </div>

              <div className="flex items-baseline gap-3">
                <Amount
                  value={BigInt(payment.amountCents)}
                  currency
                  tone={payment.reversed ? 'neutral' : 'positive'}
                />
                {/* Product principle 4: the figure reaches the transaction that caused it in one
                    click — the same drill-down a bill's postings use. */}
                <Link
                  to="/financial/transactions"
                  search={{ q: payment.transactionRef }}
                  className="text-[0.75rem] tabular-nums text-ink-muted hover:text-accent hover:underline"
                >
                  {payment.transactionRef}
                </Link>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function Tile({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0 rounded-md bg-surface-sunk px-3 py-2.5">
      <dt className="text-[0.75rem] text-ink-muted">{label}</dt>
      <dd className="mt-0.5 truncate tabular-nums">{children}</dd>
    </div>
  );
}

/**
 * Withdrawing the charge — behind a button, because it is rare and cannot be undone.
 *
 * A reason box that is always open gives a one-in-fifty action the same weight as the page's
 * everyday ones, and invites somebody to start typing into it. The reason is still required, and
 * still what the tenant holding a copy is told.
 */
function WithdrawPanel({
  invoice,
  onWithdrawn,
}: {
  invoice: TenancyInvoiceDetail;
  onWithdrawn: () => void;
}) {
  const [opened, setOpened] = useState(false);
  const [reason, setReason] = useState('');

  const cancel = useMutation(
    trpc.tenancyInvoices.cancel.mutationOptions({
      meta: { errorTitle: 'Could not withdraw that invoice' },
      onSuccess() {
        setReason('');
        setOpened(false);
        onWithdrawn();
      },
    }),
  );

  return (
    <Panel className="p-5">
      <PanelHeader
        title="Withdraw it"
        subtitle="Charged in error, or waived"
        className="p-0"
        aside={
          opened ? null : (
            <Button size="sm" variant="secondary" onClick={() => setOpened(true)}>
              Withdraw
            </Button>
          )
        }
      />

      {opened ? (
        <div className="mt-3 flex flex-col gap-3">
          <Field
            label="Reason"
            required
            hint={`Kept on the record — it is what the tenant holding ${invoice.ref} is told.`}
          >
            {(props) => (
              <Textarea
                {...props}
                rows={2}
                autoFocus
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Charged to the wrong tenancy — re-raised as IV000009"
              />
            )}
          </Field>

          <div className="flex gap-2">
            <Button
              variant="danger"
              size="sm"
              onClick={() => cancel.mutate({ tenancyInvoiceId: invoice.id, reason: reason.trim() })}
              disabled={cancel.isPending || reason.trim().length < 8}
            >
              {cancel.isPending ? 'Withdrawing' : 'Withdraw invoice'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setOpened(false);
                setReason('');
              }}
            >
              Keep it
            </Button>
          </div>
        </div>
      ) : null}
    </Panel>
  );
}

/**
 * Correcting where an invoice's money goes, while nothing has been paid against it.
 *
 * WHY THIS IS AN EDIT AND NOT A CANCEL-AND-REISSUE. It is the only field on an invoice that can be
 * wrong without the TENANT'S COPY looking wrong: a water recovery keyed to the agency produces
 * exactly the document they expect, so the mistake surfaces weeks later as an owner asking where
 * their reimbursement went. Re-raising it would burn an IV number and send the tenant a second
 * copy of a charge that has not changed by a cent, leaving them holding two invoices for one debt.
 *
 * THE NEW TOTAL IS SHOWN BEFORE ANYTHING IS PRESSED, and that is the part of this panel that earns
 * its space. A registered agency's own charges carry GST and a recovery never does, so moving a
 * $350 charge to the agency takes what the tenant owes to $402.50 — the right figure, and one that
 * no longer matches the copy they were sent. An operator who is not told that has quietly made the
 * document in the tenant's hands wrong.
 */
function RedirectPanel({
  invoice,
  creditTo,
  setCreditTo,
  gstRegistered,
  pending,
  justSaved,
  onConfirm,
}: {
  invoice: TenancyInvoiceDetail;
  creditTo: InvoiceCreditTarget;
  setCreditTo: (next: InvoiceCreditTarget) => void;
  gstRegistered: boolean;
  pending: boolean;
  justSaved: boolean;
  onConfirm: () => void;
}) {
  const changed = creditTo !== invoice.creditTo;
  const { before, after } = tenancyInvoiceRedirectTotals(
    BigInt(invoice.netCents),
    invoice.creditTo,
    creditTo,
    gstRegistered,
  );
  const totalMoves = changed && after.totalCents !== before.totalCents;

  return (
    <Panel className="p-5">
      <PanelHeader
        title="Where the money goes"
        subtitle="Editable until a payment arrives"
        className="p-0 pb-3"
      />

      <div className="flex flex-col gap-3">
        <Field label="Credit it to" hint={INVOICE_CREDIT_TARGET_HINTS[creditTo]}>
          {(props) => (
            <Select
              {...props}
              value={creditTo}
              onChange={(e) => {
                setCreditTo(e.target.value as InvoiceCreditTarget);
              }}
            >
              {INVOICE_CREDIT_TARGETS.map((value) => (
                <option key={value} value={value}>
                  {INVOICE_CREDIT_TARGET_LABELS[value]}
                </option>
              ))}
            </Select>
          )}
        </Field>

        {totalMoves ? (
          /*
           * The loudest thing on the panel, deliberately. Everything else here is a correction
           * nobody outside the agency can see; this is the one case where the tenant is now
           * holding a document that states the wrong sum.
           */
          <p className="rounded-md bg-warn-soft px-3 py-2 text-[0.8125rem] text-ink">
            The tenant would owe{' '}
            <Amount value={after.totalCents} currency className="font-semibold" /> instead of{' '}
            <Amount value={before.totalCents} currency className="font-semibold" />
            {after.gstCents > before.gstCents ? (
              <>
                {' '}
                — the agency's own charges carry GST, so{' '}
                <Amount value={after.gstCents} currency tone="neutral" /> goes on top.
              </>
            ) : (
              <>
                {' '}
                — a recovery is not a supply by anybody, so the{' '}
                <Amount value={before.gstCents} currency tone="neutral" /> of GST comes off.
              </>
            )}{' '}
            {invoice.sends.length > 0
              ? `They were already sent ${invoice.ref} — send it again after saving.`
              : `Send ${invoice.ref} after saving.`}
          </p>
        ) : null}

        <SaveButton
          type="button"
          onClick={onConfirm}
          pending={pending}
          saved={justSaved}
          disabled={!changed}
        >
          Save destination
        </SaveButton>
      </div>
    </Panel>
  );
}

function NotFound({ title, children }: { title: string; children: ReactNode }) {
  return (
    <RecordNotFound
      record="Tenancy invoice"
      title={title}
      back={{
        label: 'Back to tenancy invoices',
        link: { to: '/bills', search: { tab: 'tenancy' } },
      }}
    >
      {children}
    </RecordNotFound>
  );
}
